import { SimNPC, CardStat, isCardForbidden } from '../types/SimNPC';
import { getAvailableCards, CardPoolConfig } from '../utils/CardFilter';
import type { ExtendedCardData } from '../../utils/cardManager';
import type { CardWithId } from '../../types/card';

// 同名カードの最大枚数
const MAX_SAME_CARD = 3;

/**
 * デッキ遺伝（勝率ベース）
 * - 親1から勝率上位15枚を選択
 * - 親2から勝率上位15枚を選択
 * - 残り10枚:
 *   - 5枚は全カードプールからランダム（突然変異）
 *   - 5枚は勝率が高い親から優先的に選択
 * = 合計40枚
 * 禁忌カードはデッキ全体で1枚まで
 * 同名カードは3枚まで
 * @param parent1 親1
 * @param parent2 親2
 * @param allCards 全カードリスト
 * @param cardPoolConfig カードプール設定（除外カード等）
 */
export function breedDecks(
  parent1: SimNPC,
  parent2: SimNPC,
  allCards: ExtendedCardData[],
  cardPoolConfig?: CardPoolConfig
): CardWithId[] {
  // 利用可能なカードをフィルタリング
  const availableCards = getAvailableCards(allCards, cardPoolConfig);
  const childDeck: CardWithId[] = [];
  let forbiddenCount = 0;
  const cardCount = new Map<string, number>(); // カードID → 枚数

  /**
   * カードをデッキに追加（制限チェック付き）
   * @returns 追加できたかどうか
   */
  const tryAddCard = (card: CardWithId, parent: SimNPC): boolean => {
    const currentCount = cardCount.get(card.id) || 0;

    // 同名カード3枚制限
    if (currentCount >= MAX_SAME_CARD) {
      return false;
    }

    // 禁忌カード1枚制限
    if (isCardForbidden(card) && forbiddenCount >= 1) {
      return false;
    }

    // 追加OK
    if (isCardForbidden(card)) forbiddenCount++;
    cardCount.set(card.id, currentCount + 1);
    childDeck.push(card);
    return true;
  };

  /**
   * 制限に引っかからない代替カードを選択
   */
  const selectValidReplacement = (
    parent: SimNPC,
    cards: ExtendedCardData[]
  ): CardWithId | null => {
    // まず親のデッキから有効なカードを探す
    const validFromParent = parent.deck.filter((c) => {
      const count = cardCount.get(c.id) || 0;
      if (count >= MAX_SAME_CARD) return false;
      if (isCardForbidden(c) && forbiddenCount >= 1) return false;
      return true;
    });

    if (validFromParent.length > 0) {
      const card =
        validFromParent[Math.floor(Math.random() * validFromParent.length)];
      return {
        ...card,
        uniqueId: `${card.id}_replacement_${Math.random()}`,
      };
    }

    // 親のデッキにない場合は利用可能カードプールから
    const validFromAll = cards.filter((c) => {
      const count = cardCount.get(c.id) || 0;
      if (count >= MAX_SAME_CARD) return false;
      if (isCardForbidden(c) && forbiddenCount >= 1) return false;
      return true;
    });

    if (validFromAll.length > 0) {
      const card = validFromAll[Math.floor(Math.random() * validFromAll.length)];
      return {
        ...card,
        uniqueId: `${card.id}_replacement_${Math.random()}`,
      };
    }

    return null;
  };

  // 1. 親1のデッキから勝率上位15枚を選択
  const fromParent1 = selectTopCardsByWinRate(parent1, 15);
  for (const card of fromParent1) {
    if (!tryAddCard(card, parent1)) {
      // 制限に引っかかった場合は代替カードを追加
      const replacement = selectValidReplacement(parent1, availableCards);
      if (replacement) {
        tryAddCard(replacement, parent1);
      }
    }
  }

  // 2. 親2のデッキから勝率上位15枚を選択
  const fromParent2 = selectTopCardsByWinRate(parent2, 15);
  for (const card of fromParent2) {
    if (!tryAddCard(card, parent2)) {
      // 制限に引っかかった場合は代替カードを追加
      const replacement = selectValidReplacement(parent2, availableCards);
      if (replacement) {
        tryAddCard(replacement, parent2);
      }
    }
  }

  // 3. 残り枚数を計算して埋める（40枚になるまで）
  const remaining = 40 - childDeck.length;

  // 利用可能カードプールから5枚
  const fromPoolCount = Math.min(5, remaining);
  for (let i = 0; i < fromPoolCount; i++) {
    const card = selectFromCardPool(availableCards, cardCount, forbiddenCount);
    if (card) {
      if (tryAddCard(card, parent1)) {
        // 追加成功
      }
    }
  }

  // 残りは勝率が高い親から優先的に選択
  const remainingAfterPool = 40 - childDeck.length;
  const winnerParent = getHigherWinRateParent(parent1, parent2);
  const loserParent = winnerParent === parent1 ? parent2 : parent1;

  for (let i = 0; i < remainingAfterPool; i++) {
    // まず勝率が高い親から試す
    let replacement = selectFromParentDeck(winnerParent, cardCount, forbiddenCount);
    // なければ勝率が低い親から
    if (!replacement) {
      replacement = selectFromParentDeck(loserParent, cardCount, forbiddenCount);
    }
    // それでもなければ利用可能カードプールから
    if (!replacement) {
      replacement = selectFromCardPool(availableCards, cardCount, forbiddenCount);
    }
    if (replacement) {
      tryAddCard(replacement, parent1);
    }
  }

  // 4. シャッフル
  shuffle(childDeck);

  return childDeck;
}

/**
 * 親のデッキから勝率上位n枚を選択
 * 統計がない場合はランダム選択にフォールバック
 *
 * 枚数考慮: 親が同じカードを2枚持っていれば、2枚とも遺伝候補になる
 */
function selectTopCardsByWinRate(parent: SimNPC, count: number): CardWithId[] {
  // 全カード（重複含む）に勝率を付与
  const cardsWithRate = parent.deck.map((card) => {
    const stat = parent.cardStats.get(card.id);
    let winRate = 0.5; // デフォルト勝率

    if (stat && stat.gamesWithCard >= 3) {
      // 3戦以上の統計がある場合は実際の勝率を使用
      winRate = stat.winsWithCard / stat.gamesWithCard;
    }

    return { card, winRate };
  });

  // 勝率順にソート（高い順）、同率の場合はランダム
  cardsWithRate.sort((a, b) => {
    if (b.winRate !== a.winRate) {
      return b.winRate - a.winRate;
    }
    return Math.random() - 0.5; // 同率ならランダム
  });

  // 上位n枚を選択
  const selected = cardsWithRate.slice(0, count);

  // 結果を返す（新しいuniqueIdを付与）
  const result: CardWithId[] = selected.map((item, i) => ({
    ...item.card,
    uniqueId: `${item.card.id}_inherited_${i}_${Math.random()}`,
  }));

  return result;
}

/**
 * 勝率が高い親を返す
 */
function getHigherWinRateParent(parent1: SimNPC, parent2: SimNPC): SimNPC {
  const getWinRate = (npc: SimNPC): number => {
    const total = npc.wins + npc.losses;
    if (total === 0) return 0.5; // 対戦履歴がなければ50%
    return npc.wins / total;
  };

  const rate1 = getWinRate(parent1);
  const rate2 = getWinRate(parent2);

  // 同率ならランダム
  if (rate1 === rate2) {
    return Math.random() < 0.5 ? parent1 : parent2;
  }

  return rate1 > rate2 ? parent1 : parent2;
}

/**
 * カードプールからランダムに有効なカードを選択
 */
function selectFromCardPool(
  cards: ExtendedCardData[],
  cardCount: Map<string, number>,
  forbiddenCount: number
): CardWithId | null {
  const validCards = cards.filter((c) => {
    const count = cardCount.get(c.id) || 0;
    if (count >= MAX_SAME_CARD) return false;
    if (isCardForbidden(c) && forbiddenCount >= 1) return false;
    return true;
  });

  if (validCards.length === 0) return null;

  const card = validCards[Math.floor(Math.random() * validCards.length)];
  return {
    ...card,
    uniqueId: `${card.id}_pool_${Math.random()}`,
  };
}

/**
 * 親のデッキからランダムに有効なカードを選択
 */
function selectFromParentDeck(
  parent: SimNPC,
  cardCount: Map<string, number>,
  forbiddenCount: number
): CardWithId | null {
  const validCards = parent.deck.filter((c) => {
    const count = cardCount.get(c.id) || 0;
    if (count >= MAX_SAME_CARD) return false;
    if (isCardForbidden(c) && forbiddenCount >= 1) return false;
    return true;
  });

  if (validCards.length === 0) return null;

  const card = validCards[Math.floor(Math.random() * validCards.length)];
  return {
    ...card,
    uniqueId: `${card.id}_parent_${Math.random()}`,
  };
}

/**
 * 配列からランダムにn個サンプリング（フォールバック用）
 */
function sampleCards(cards: CardWithId[], count: number): CardWithId[] {
  const shuffled = [...cards].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count).map((card, i) => ({
    ...card,
    uniqueId: `${card.id}_inherited_${i}_${Math.random()}`,
  }));
}

/**
 * 配列をシャッフル
 */
function shuffle<T>(array: T[]): void {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}
